// @ts-nocheck
// ─────────────────────────────────────────────────────────────────────
// store · Tiny reactive store for Blyn
//
// API:
//   import { store } from './state/store';
//
//   store.get()                 → current state snapshot
//   store.get('currentView')    → single value
//   store.set({ currentView: 'projects' })  → merge update
//   store.update('notifications', n => [...n, newOne]) → functional update
//   store.subscribe(fn)         → fn(state) called on every change
//   store.select(keyOrFn, fn)   → fn called only when that slice changes
//
// Why not Redux/Zustand: This is a single-file prototype. The store is
// 100 lines and does what we need. Reactive subscriptions, slice
// selectors, immutable-ish updates. Real production: swap for Zustand
// without touching consumers if API is preserved.
// ─────────────────────────────────────────────────────────────────────

const _initial = {
  // Host config
  apiBaseUrl: null as string | null,
  routerMode: 'browser' as 'browser' | 'memory',
  onShareCallback: null as ((payload: { conversationId?: string; projectId?: string; title?: string }) => void) | null,

  // Navigation
  currentView: 'conversation',
  previousView: null,
  currentProjectId: null,
  currentConversationId: null,

  // UI mode
  theme: 'dark',                  // 'dark' | 'light'
  mode: 'simple',                 // 'simple' | 'expert'

  // Overlays
  activeModal: null,              // modal id or null
  activeDrawer: null,
  paletteOpen: false,
  helpOpen: false,
  helpViewId: null as string | null,
  mobileRailOpen: false,

  // Per-view sub-state
  workspaceTab: 'overview',       // overview|conversations|assets|audiences|journeys|staged|activity
  settingsTab: 'memory',
  insightType: 'drops',           // drops|persona|propensity|channel|creative|geographic|timing|offer|attribution
  wizardStep: 1,

  // Mutable lists (populated lazily by views on first import to avoid circular deps)
  toasts: [],                     // [{id, kind, title, sub, expiresAt}]
  stagedActions: [],              // empty for no-data mode
  notifications: [],
  approvalsQueue: [],             // empty for no-data mode
  dismissedNudgeIds: [],
  permanentlyDismissedNudgeIds: [],

  // Memory (populated lazily from data/memory)
  memoryFacts: null,
  memoryPatterns: null,

  // Running tasks (empty for no-data mode)
  tasks: [],
  trackRecord: [],
  tasksFilter: 'active',          // active | completed | all
  selectedTaskId: null,

  // Onboarding wizard state
  onboardingComplete: null,       // null = never seen; false = in progress; true = done
  onboardingHydrated: false,      // true once GET /onboarding has resolved
  onboardingPreviewMode: false,   // true while replaying wizard locally (no API sync)
  onboardingStep: 1,
  onboardingChoices: {},

  // Composer state. Stage mode is per-conversation; legacy `composerStageMode`
  // is kept as a global fallback for back-compat with views that haven't
  // migrated to the per-conversation selectors yet.
  composerStageMode: false,
  composerStageModeByConvId: {},

  // Selection state
  selectedNode: null,             // journey editor selected node
  selectedAudience: null,
  composerDraft: '',
  composerDraftSeed: null as string | null,
  searchQuery: '',
  searchResults: [],

  // Filters
  notifFilter: 'all',
  auditFilters: { range: '30days', user: 'all', type: 'all' },
  projectsFilter: 'all',          // all|live|draft|archived

  // Filters and drafts for the 4 ARC-integrated views (creatives,
  // template editor, web personalization, product catalog).
  selectedCreativeId: null,
  creativesFilter: 'all',         // all|email|sms|dm
  editingCreativeId: null,
  templateDraft: null,            // { __creativeId, body }
  webPagesFilter: 'all',          // all|page_home|page_pdp|page_checkout
  productsFilter: 'all',          // all|status:<x>|cat:<x>
  productsSearch: '',
  highlightedProductId: null,     // set by arc.product.selected, auto-clears after 8s
  highlightedProductReason: null,

  // ───────────────────────────────────────────────────────────────────
  // Current user · used by the new-conversation hero greeting
  // ───────────────────────────────────────────────────────────────────
  currentUser: {
    id: 'tabrez',
    firstName: 'Tabrez',
    lastName: 'Anwar',
    initial: 'T',
    role: 'admin',
  },

  // ───────────────────────────────────────────────────────────────────
  // Conversational starters · per data/starters + shared/starters
  // ───────────────────────────────────────────────────────────────────
  // Scope chip: null = follow currentProjectId · 'tenant' or 'none'
  // means the user explicitly overrode the default. The starter system
  // re-ranks and re-materializes prompts when this changes.
  scopeChipMode:        null,
  // Pending starter to seed into the conversation composer · consumed
  // by conversation on mount and cleared.
  pendingStarter:       null,        // { id, text, caretPosition, source }
  // Whether the composer carousel is dismissed for this session
  composerCarouselDismissed: false,

  // ───────────────────────────────────────────────────────────────────
  // ARC store keys · per Integration Implementation Guide v1.0 §5.2.
  // These mirror ARC's reactive store. Populated by ARC events (see
  // shared/arcIntegration) or read directly via store.get(key).
  // Null = not yet hydrated; views should handle the null case.
  // ───────────────────────────────────────────────────────────────────
  metrics:           null,        // { revenue, profit, identities, roas, ... }
  runs:              null,        // active/draft/completed campaign runs
  audiences:         null,        // audience segments with criteria
  experiments:       null,        // A/B and multivariate tests
  creatives:         null,        // creative templates and variants
  agents:            null,        // agent roster with health metrics
  // stagedActions: defined above; ARC populates via ACTION_PROPOSED
  // notifications: defined above; ARC populates via ALERT_GENERATED
  integrations:      null,        // connected external data sources
  channelConfigs:    null,        // per-channel delivery configuration
  crossChannelRules: null,        // global coordination rules
  variableSchema:    null,        // 35+ variable definitions for templates
  products:          null,        // product catalog
  objectives:        null,        // business objectives with weighting
  forecast:          null,        // revenue/profit predictions
  // trackRecords: defined above (legacy key: trackRecord)
  autonomy:          null,        // per-channel autonomy levels (L0-L3)
  settings:          null,        // tenant settings (id, name, timezone, etc.)
  auditLog:          null,        // immutable audit trail
  webPages:          null,        // web personalization pages and zones
};

let _state = { ..._initial };
const _subs = new Set();
const _sliceSubs = new Map(); // key → Set<{key, last, fn}>

function _notify(prev) {
  // Full subscribers
  for (const fn of _subs) {
    try { fn(_state, prev); } catch (e) { console.error('store sub error:', e); }
  }
  // Slice subscribers. Only fire if their slice changed
  for (const [key, subs] of _sliceSubs.entries()) {
    if (_state[key] !== prev[key]) {
      for (const s of subs) {
        try { s.fn(_state[key], prev[key]); } catch (e) { console.error(e); }
        s.last = _state[key];
      }
    }
  }
}

export const store = {
  get(key) {
    return key ? _state[key] : _state;
  },

  set(patch) {
    const prev = _state;
    _state = { ..._state, ...patch };
    _notify(prev);
  },

  update(key, fn) {
    const prev = _state;
    _state = { ..._state, [key]: fn(_state[key]) };
    _notify(prev);
  },

  subscribe(fn) {
    _subs.add(fn);
    return () => _subs.delete(fn);
  },

  // Subscribe to a single slice. Fn fires only when that key changes
  select(key, fn) {
    if (!_sliceSubs.has(key)) _sliceSubs.set(key, new Set());
    const entry = { key, fn, last: _state[key] };
    _sliceSubs.get(key).add(entry);
    // Fire once with current value
    fn(_state[key], undefined);
    return () => _sliceSubs.get(key)?.delete(entry);
  },

  // For debugging
  reset() {
    const prev = _state;
    _state = { ..._initial };
    _notify(prev);
  },

  // For dev tools / time-travel debugging later
  _snapshot() {
    return JSON.parse(JSON.stringify(_state));
  },
};

// Expose for browser console debugging
if (typeof window !== 'undefined') {
  window.__store = store;
}
